import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { apiClient } from '../../../services/api';
import type { InvoiceRecord } from '../../cashflow/types';
import { getFoldDetail } from '../data';
import { CATEGORY, fmtDate, inrFull, reconOf, TODO_OWNER, verdictOf } from '../display';
import { FoldDetail } from '../types';

/**
 * Screen 2 — the invoice detail. Slide-over, organised into THREE zones by the
 * question each answers (not a flat card grid):
 *   1. VERDICT  — is this a real problem? (banner + headline + action)
 *   2. PROOF    — don't trust it? here's the evidence + files + bank refs
 *   3. CONTEXT  — operational detail: owner, money, actions, sentiment, flow
 * Zone 3 is collapsible so the verdict + proof lead.
 */

interface Props {
	invoiceNumber: string | null;
	/** master row (already loaded by the list) — feeds the Zoho card; null until depth lands. */
	depthRow?: InvoiceRecord | null;
	/** open the feedback dialog (passes the analysed thread id when available). */
	onFlag?: (threadId?: string | null) => void;
	onClose: () => void;
}

const leakClassOf = (verdict: string): 'REAL' | 'LIKELY' | 'PARTIAL' | 'NOT_REAL' => {
	const v = (verdict || '').toUpperCase();
	if (v.startsWith('REAL')) return 'REAL';
	if (v.startsWith('LIKELY')) return 'LIKELY';
	if (v.startsWith('PARTIAL')) return 'PARTIAL';
	return 'NOT_REAL';
};

async function openFile(url: string) {
	try {
		const rel = url.replace(/^\/v1\/api/, '');
		const res = await apiClient.get(rel, { responseType: 'blob' });
		const blobUrl = URL.createObjectURL(res.data as Blob);
		window.open(blobUrl, '_blank', 'noopener,noreferrer');
		setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
	} catch {
		/* nothing to open */
	}
}

export const InvoiceDetail: React.FC<Props> = ({ invoiceNumber, depthRow, onFlag, onClose }) => {
	const [data, setData] = useState<FoldDetail | null>(null);
	const [loading, setLoading] = useState(false);
	const [showContext, setShowContext] = useState(true);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
		if (invoiceNumber) document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [invoiceNumber, onClose]);

	useEffect(() => {
		if (!invoiceNumber) {
			setData(null);
			return;
		}
		let active = true;
		setLoading(true);
		getFoldDetail(invoiceNumber)
			.then(d => active && setData(d))
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
		};
	}, [invoiceNumber]);

	if (!invoiceNumber) return null;
	const v = data?.recon_class
		? reconOf(data.recon_class)
		: data
			? verdictOf(leakClassOf(data.leak_verdict))
			: verdictOf('NOT_REAL');
	const ag = data?.agentic;

	return (
		<div className='fixed inset-0 z-50 flex justify-end bg-black/40' onClick={onClose}>
			<div
				className='flex h-full w-full max-w-3xl flex-col bg-slate-50 shadow-2xl'
				onClick={e => e.stopPropagation()}
			>
				{loading || !data ? (
					<div className='flex flex-1 flex-col items-center justify-center text-slate-400'>
						<Loader2 className='mb-3 h-7 w-7 animate-spin' />
						<span className='text-sm'>Loading invoice…</span>
					</div>
				) : (
					<>
						{/* ── HEADER ── */}
						<div className='border-b border-slate-200 bg-white px-6 py-5'>
							<div className='flex items-start justify-between'>
								<div>
									<h2 className='font-mono text-xl font-bold tracking-tight text-slate-900'>{data.invoice}</h2>
									<div className='mt-1.5 flex flex-wrap items-center gap-2 text-sm text-slate-500'>
										{ag?.relevance?.category && (
											<span className={cn('rounded px-1.5 py-0.5 text-[11px] font-semibold', CATEGORY[ag.relevance.category]?.cls || 'bg-slate-100 text-slate-500')}>
												{CATEGORY[ag.relevance.category]?.label || ag.relevance.category}
											</span>
										)}
										<span className='font-medium text-slate-600'>{data.customer}</span>
										<span className='text-slate-300'>·</span>
										<span className='font-semibold tabular-nums text-slate-700'>{inrFull(data.amount_at_stake)}</span>
										<span className='text-slate-300'>·</span>
										<span>folded over {data.n_threads_total} emails</span>
										{data.has_contradiction && <span className='text-amber-600' title='emails disagree'>⚠ contradiction</span>}
									</div>
								</div>
								<button onClick={onClose} className='rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700' aria-label='Close'>✕</button>
							</div>
						</div>

						<div className='flex-1 space-y-5 overflow-y-auto p-6'>
							{/* ══ ZONE 1: THE VERDICT ══ */}
							<div className={cn('overflow-hidden rounded-xl border-l-4 bg-white shadow-sm', v.bar.replace('bg-', 'border-'))}>
								<div className='p-4'>
									<div className='flex items-center gap-2'>
										<span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', v.pill)}>{v.label}</span>
										<span className={cn('text-[15px] font-bold', v.text)}>
											{data.recon_headline || data.leak_verdict}
										</span>
										{onFlag && (
											<button
												onClick={() => onFlag(ag?.thread_id ?? null)}
												className='ml-auto whitespace-nowrap text-xs text-slate-400 hover:text-[#b02d29] hover:underline'
											>
												⚑ Wrong analysis?
											</button>
										)}
									</div>
									{ag?.actions?.headline && (
										<div className='mt-2.5 text-sm font-semibold text-slate-800'>→ {ag.actions.headline}</div>
									)}
								</div>
							</div>

							{/* Zoho vs Email — the system of record next to what the client says (spec §5.2) */}
							{(depthRow || data.zoho_status) && (
								<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
									<Card title='◆ Zoho — system of record'>
										<KV label='Status'><b className={/overdue/i.test(depthRow?.z?.status || data.zoho_status || '') ? 'text-red-600' : 'text-slate-800'}>{depthRow?.z?.status || data.zoho_status || '—'}</b></KV>
										<KV label='Balance outstanding'><b className='tabular-nums text-red-600'>{inrFull(depthRow?.z?.bal ?? data.zoho_balance)}</b></KV>
										{depthRow?.z?.total != null && <KV label='Invoice total'><span className='tabular-nums'>{inrFull(depthRow.z.total)}</span></KV>}
										{depthRow?.z?.idate && <KV label='Raised on'>{fmtDate(depthRow.z.idate)}</KV>}
										{depthRow?.z?.due && (
											<KV label='Due'>
												{fmtDate(depthRow.z.due)}
												{(depthRow.z.od ?? 0) > 0 && <span className='ml-1.5 font-bold text-red-600'>+{depthRow.z.od}d overdue</span>}
											</KV>
										)}
										<KV label='Payment recorded'>
											{depthRow?.z?.paydate ? (
												<span>{fmtDate(depthRow.z.paydate)}{depthRow.z.payamt ? ` · ${inrFull(depthRow.z.payamt)}` : ''}{depthRow.z.paymode ? ` · ${depthRow.z.paymode}` : ''}</span>
											) : (
												<b className='text-red-600'>not recorded</b>
											)}
										</KV>
									</Card>
									<Card title='◆ Email — what the client says'>
										<KV label='Customer claims paid'>
											<b className={depthRow?.cpaid ? 'text-amber-600' : 'text-slate-800'}>{depthRow ? (depthRow.cpaid ? 'yes' : 'no') : data.email_has_proof ? 'yes (with proof)' : '—'}</b>
										</KV>
										<KV label='Strongest proof'>{depthRow?.tier ? depthRow.tier.replace(/_/g, ' ') : 'none for this invoice'}</KV>
										{depthRow?.c?.ball && <KV label='Ball in court'>{String(depthRow.c.ball).replace(/_/g, ' ')}</KV>}
										{depthRow?.c?.sent && <KV label='Sentiment'>{depthRow.c.sent}</KV>}
										{depthRow?.c?.quiet != null && <KV label='Quiet for'><span className={cn('tabular-nums', depthRow.c.quiet > 30 && 'font-bold text-red-600')}>{depthRow.c.quiet} days</span></KV>}
									</Card>
								</div>
							)}

							{/* ══ ZONE 2: THE PROOF ══ */}
							<ZoneLabel>The proof</ZoneLabel>

							{/* Evidence chain */}
							<Card title='Evidence chain' subtitle='one row per email conversation — why the verdict is what it is'>
								<div className='space-y-2'>
									{data.evidence_facts.map((f, i) => (
										<div key={i} className='rounded-lg border border-slate-100 p-3'>
											<div className='mb-1 flex items-center gap-2'>
												<span className={cn('rounded px-1.5 py-0.5 text-[10px] font-bold', f.verdict.includes('disput') ? 'bg-red-100 text-red-700' : f.verdict.includes('paid') ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
													{f.verdict.replace(/_/g, ' ')}
												</span>
												<span className='font-mono text-[10px] text-slate-300'>{f.thread_id.slice(0, 12)}</span>
												<span className='ml-auto text-[10px] uppercase tracking-wide text-slate-400'>{f.confidence}</span>
											</div>
											<div className='text-sm leading-snug text-slate-700'>{f.evidence_chain}</div>
											{f.utr && <div className='mt-1 font-mono text-[11px] font-bold text-blue-700'>UTR: {f.utr}</div>}
										</div>
									))}
								</div>
							</Card>

							{/* Bank references / bulk */}
							{(data.utrs_found?.some(u => u && !/^none/i.test(u)) || data.bulk_payment_siblings.length > 0) && (
								<Card title='Bank references' subtitle='UTR / NEFT + bulk-payment coverage'>
									<div className='flex flex-wrap gap-1.5'>
										{(data.utrs_found || []).filter(u => u && !/^none/i.test(u)).map((u, i) => (
											<span key={i} className='rounded bg-blue-50 px-2 py-0.5 font-mono text-[11px] text-blue-700'>{u}</span>
										))}
									</div>
									{data.bulk_payment_siblings.length > 0 && (
										<div className='mt-3 rounded-lg bg-violet-50 p-3 text-sm text-violet-900'>
											<b>This payment also covers {data.bulk_payment_siblings.length} other invoice(s)</b> — don't double-count:
											<ul className='mt-1.5 space-y-1'>
												{data.bulk_payment_siblings.map((s, i) => (
													<li key={i} className='flex justify-between font-mono text-[11px]'>
														<span>{s.invoice_number}</span>
														<span className='tabular-nums'>{inrFull(s.amount_at_stake)}</span>
													</li>
												))}
											</ul>
										</div>
									)}
								</Card>
							)}

							{/* Attachments */}
							<Card title='Attachments' subtitle={ag?.attachments_meta ? `real files — ${ag.attachments_meta.noise_ignored || 0} noise hidden` : 'invoice PDFs & payment proof'}>
								{data.files && data.files.length > 0 ? (
									<div className='space-y-1'>
										{data.files.map((f, i) => (
											<div key={i} className='flex items-center gap-2 rounded-lg border border-slate-100 px-2.5 py-1.5 text-sm'>
												<span className='text-slate-400'>{(f.content_type || '').includes('pdf') ? '📄' : (f.content_type || '').includes('image') ? '🖼️' : '📎'}</span>
												<span className='flex-1 truncate text-slate-700' title={f.filename}>{f.filename}</span>
												{f.invoice_related && <span className='rounded bg-blue-50 px-1 text-[9px] text-blue-700'>invoice</span>}
												{f.size_kb ? <span className='text-[10px] text-slate-400'>{f.size_kb} KB</span> : null}
												{f.url ? (
													<button onClick={() => openFile(f.url!)} className='text-xs text-blue-600 hover:underline'>open ↗</button>
												) : (
													<span className='text-[10px] text-slate-300'>no link</span>
												)}
											</div>
										))}
									</div>
								) : (
									<div className='text-sm text-slate-400'>No files attached to this conversation.</div>
								)}
							</Card>

							{/* ══ ZONE 3: THE CONTEXT (collapsible) ══ */}
							{ag && (ag.owner || ag.money || ag.actions || ag.sentiment || ag.flow) ? (
								<>
									<button
										onClick={() => setShowContext(s => !s)}
										className='flex w-full items-center justify-between text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 hover:text-slate-600'
									>
										<span>The context {showContext ? '▾' : '▸'}</span>
										<span className='font-normal normal-case tracking-normal text-slate-400'>who's handling it · money · actions</span>
									</button>
									{showContext && <ContextZone ag={ag} />}
								</>
							) : (
								<div className='rounded-xl border border-dashed border-slate-200 p-4 text-center text-sm text-slate-400'>
									Deep analysis pending for this invoice.
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</div>
	);
};

// ── Zone 3 cards (full 7-agent / pilot parity) ──
const ContextZone: React.FC<{ ag: NonNullable<FoldDetail['agentic']> }> = ({ ag }) => (
	<div className='space-y-4'>
		{/* Supervisor cross-check — the trust line ("all agents passed") */}
		{ag.supervisor?.overall && (
			<div
				className={cn(
					'flex items-center gap-2 rounded-xl border px-4 py-2.5 text-[12px]',
					ag.supervisor.overall === 'pass' ? 'border-emerald-200 bg-emerald-50/60 text-emerald-800' : 'border-amber-200 bg-amber-50/60 text-amber-800'
				)}
			>
				<span className='font-bold'>★ Supervisor: {ag.supervisor.overall}</span>
				<span className='text-slate-500'>
					{ag.supervisor.verdicts
						? `${Object.values(ag.supervisor.verdicts).filter(v => v === 'pass').length}/${Object.keys(ag.supervisor.verdicts).length} agents passed`
						: 'cross-checked against Zoho facts + thread evidence'}
				</span>
				{ag.message_count ? <span className='ml-auto font-mono text-[10px] text-slate-400'>over {ag.message_count} messages</span> : null}
			</div>
		)}

		{/* Actions — the most important context card, three columns */}
		{ag.actions && (ag.actions.our_done?.length || ag.actions.todo?.length || ag.actions.client_actions?.length) ? (
			<Card title='Actions'>
				<div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
					<ActionCol title='✓ We have done' tone='emerald' items={(ag.actions.our_done || []).map(a => ({ main: a.action, sub: a.by ? `by ${a.by}` : undefined, quote: a.evidence }))} />
					<ActionCol title='↩ Client said' tone='amber' items={(ag.actions.client_actions || []).map(a => ({ main: a.action, sub: a.by ? `by ${a.by}` : undefined, quote: a.evidence }))} />
					<ActionCol title='▲ To do' tone='blue' items={(ag.actions.todo || []).map(a => ({ main: a.action, sub: a.why, ownerTag: a.owner }))} />
				</div>
			</Card>
		) : null}

		<div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
			{/* Money */}
			{ag.money && (
				<Card title='Money'>
					<div className='grid grid-cols-2 gap-3'>
						<div className='rounded-lg border border-emerald-200 bg-emerald-50/60 p-3'>
							<div className='text-[10px] font-bold uppercase tracking-wide text-slate-500'>They owe us</div>
							<div className='mt-0.5 text-base font-bold text-emerald-700'>{inrFull(ag.money.ar_they_owe_us?.amount)}</div>
							{ag.money.ar_they_owe_us?.basis && <div className='mt-1 text-[10.5px] leading-snug text-slate-500'>{ag.money.ar_they_owe_us.basis}</div>}
						</div>
						<div className='rounded-lg border border-amber-200 bg-amber-50/60 p-3'>
							<div className='text-[10px] font-bold uppercase tracking-wide text-slate-500'>We owe them</div>
							<div className='mt-0.5 text-base font-bold text-amber-700'>{inrFull(ag.money.ap_we_owe_them?.amount)}</div>
							{ag.money.ap_we_owe_them?.basis && <div className='mt-1 text-[10.5px] leading-snug text-slate-500'>{ag.money.ap_we_owe_them.basis}</div>}
						</div>
					</div>
					{ag.money.net_direction && (
						<div className='mt-2 text-xs text-slate-500'>Net: <b className='text-slate-700'>{ag.money.net_direction.replace(/_/g, ' ')}</b></div>
					)}
				</Card>
			)}

			{/* Owner */}
			{ag.owner && (
				<Card title='Owner'>
					<KV label='Handling'><b className='text-slate-800'>{ag.owner.internal_owner || '—'}</b></KV>
					{ag.owner.owner_email && <KV label='Email'><span className='font-mono text-xs'>{ag.owner.owner_email}</span></KV>}
					{ag.owner.started_by && <KV label='Started by'>{ag.owner.started_by}</KV>}
					{ag.owner.also_involved_internal?.length ? (
						<KV label='Also involved'><span className='text-xs'>{ag.owner.also_involved_internal.slice(0, 3).join(', ')}{ag.owner.also_involved_internal.length > 3 ? ` +${ag.owner.also_involved_internal.length - 3}` : ''}</span></KV>
					) : null}
				</Card>
			)}

			{/* Sentiment */}
			{ag.sentiment && (
				<Card title='Sentiment'>
					<div className='flex items-center gap-2'>
						<span className={cn('rounded px-2 py-0.5 text-[11px] font-semibold', /tense|negativ|frustrat/i.test(ag.sentiment.overall || '') ? 'bg-red-50 text-red-700' : /neutral|stable/i.test(ag.sentiment.overall || '') ? 'bg-slate-100 text-slate-600' : 'bg-emerald-50 text-emerald-700')}>
							{ag.sentiment.overall}
						</span>
						{ag.sentiment.trajectory && <span className='text-xs text-slate-500'>· {ag.sentiment.trajectory}</span>}
					</div>
					{ag.sentiment.one_line && <div className='mt-2 text-sm leading-snug text-slate-600'>{ag.sentiment.one_line}</div>}
				</Card>
			)}

			{/* Flow */}
			{ag.flow?.flow?.length ? (
				<Card title='Conversation flow'>
					<ol className='space-y-2'>
						{ag.flow.flow.slice(0, 8).map((s, i) => (
							<li key={i} className='flex gap-2.5 text-sm'>
								<span className='mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-300' />
								<div>
									<span className='text-[11px] font-semibold text-slate-400'>{s.date}</span>{' '}
									<b className='text-slate-700'>{s.who}:</b> <span className='text-slate-600'>{s.what}</span>
								</div>
							</li>
						))}
					</ol>
					{ag.flow.current_state && <div className='mt-2 text-xs text-slate-500'>Now: {ag.flow.current_state}</div>}
				</Card>
			) : null}

			{/* Linkage — which invoices this conversation is about */}
			{ag.linkage?.invoices?.length ? (
				<Card title='Linked invoices'>
					<div className='space-y-1.5'>
						{ag.linkage.invoices.slice(0, 6).map((l, i) => (
							<div key={i} className='flex items-start gap-2 text-sm'>
								<span className='font-mono text-xs font-medium text-slate-700'>{l.invoice}</span>
								{l.relevance && (
									<span className={cn('rounded px-1 py-0.5 text-[9px] font-bold uppercase', l.relevance === 'primary' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500')}>
										{l.relevance}
									</span>
								)}
								{l.note && <span className='flex-1 text-[11px] leading-snug text-slate-500'>{l.note}</span>}
							</div>
						))}
					</div>
				</Card>
			) : null}
		</div>
	</div>
);

const ZoneLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div className='pt-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400'>{children}</div>
);

const Card: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
	<div className='rounded-xl border border-slate-200 bg-white p-4 shadow-sm'>
		<div className='mb-3'>
			<div className='text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500'>{title}</div>
			{subtitle && <div className='text-[10.5px] text-slate-400'>{subtitle}</div>}
		</div>
		{children}
	</div>
);

const KV: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className='flex items-center justify-between border-b border-slate-50 py-1.5 text-sm last:border-0'>
		<span className='text-slate-400'>{label}</span>
		<span className='text-slate-800'>{children}</span>
	</div>
);

const ACT_TONE = {
	emerald: 'text-emerald-700',
	amber: 'text-amber-700',
	blue: 'text-blue-700',
} as const;

const ActionCol: React.FC<{
	title: string;
	tone: keyof typeof ACT_TONE;
	items: Array<{ main: string; sub?: string; quote?: string; ownerTag?: string }>;
}> = ({ title, tone, items }) => (
	<div>
		<div className={cn('mb-2 text-[10.5px] font-bold uppercase tracking-wide', ACT_TONE[tone])}>{title}</div>
		{items.length ? (
			<div className='space-y-2'>
				{items.map((it, i) => (
					<div key={i} className='rounded-lg bg-slate-50 p-2'>
						{it.ownerTag && (
							<span className={cn('mb-1 inline-block rounded px-1 py-0.5 text-[9px] font-bold', TODO_OWNER[it.ownerTag]?.cls || 'bg-slate-200 text-slate-600')}>
								{TODO_OWNER[it.ownerTag]?.label || it.ownerTag}
							</span>
						)}
						<div className='text-[12.5px] leading-snug text-slate-700'>{it.main}</div>
						{it.sub && <div className='mt-0.5 text-[10.5px] text-slate-400'>{it.sub}</div>}
						{it.quote && <div className='mt-1 truncate text-[10px] italic text-slate-400' title={it.quote}>“{it.quote}”</div>}
					</div>
				))}
			</div>
		) : (
			<div className='text-[11px] text-slate-300'>—</div>
		)}
	</div>
);
